// Care-team assignment vocabulary and wire contracts.
//
// `source` records how a pairing arose, so months later it stays answerable
// whether a member was paired automatically, by a booking, or by hand. It ships
// with all three values from the start even though the `booking` writer lands
// with the appointments work — an enum value is cheap, a missing one is a
// migration.
//
// The `role` on an assignment is the CLINICIAN_ROLE vocabulary (the clinical
// care-team layer), deliberately distinct from `internal_users.role` (the org
// layer). Neither imports the other; a value legal in one is not automatically
// legal in the other.

import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { ClinicianRole } from '../programs/schema';

export const CARE_TEAM_ASSIGNMENT_SOURCE = ['auto', 'booking', 'manual'] as const;
export type CareTeamAssignmentSource = (typeof CARE_TEAM_ASSIGNMENT_SOURCE)[number];
export const careTeamAssignmentSourceSchema = z.enum(CARE_TEAM_ASSIGNMENT_SOURCE);

/**
 * What a care-team assignment change did, for the attribution trail.
 *
 * Four values, not Pelago 1's three. Its `MADE_PRIMARY` has no counterpart for
 * the row that LOST the primary slot, but promotion demotes the incumbent in
 * the same transaction — a real state change, made by the promoting actor, to
 * a row they never named. Without `demoted` that half stays unattributed,
 * which is the gap the trail exists to close.
 *
 * `assigned` and `unassigned` are once-per-row (a re-pairing opens a new row),
 * while `promoted`/`demoted` may recur over one row's life. That asymmetry is
 * why attribution lives in rows here rather than in columns on the assignment.
 */
export const CARE_TEAM_ASSIGNMENT_AUDIT_ACTION = [
  'assigned',
  'unassigned',
  'promoted',
  'demoted',
] as const;
export type CareTeamAssignmentAuditAction = (typeof CARE_TEAM_ASSIGNMENT_AUDIT_ACTION)[number];
export const careTeamAssignmentAuditActionSchema = z.enum(CARE_TEAM_ASSIGNMENT_AUDIT_ACTION);

/**
 * How long, measured from account creation, an unconfirmed primary stays hidden
 * from the member.
 *
 * Every member is assigned a default specialist at creation, but the member-
 * facing read keeps answering "unassigned" while that primary is provisional
 * (`confirmed_at` null) and the account is no more than this many hours old,
 * so a new member sees the care-team intro rather than a stranger's name.
 * Staff reads show the row at every age. Engagement ends the window early by
 * confirming the primary, and a confirmed primary is shown at any age.
 *
 * Applied as a read-time predicate rather than stored state: there is no job,
 * nothing to fall out of sync, and correcting `created_at` recomputes the
 * window for free. The value is expected to change after care-ops review.
 */
export const CARE_TEAM_ASSIGNMENT_PROVISIONAL_HOURS = 24;

/**
 * The clinical roles that may not hold a member's primary care-team slot.
 *
 * The primary is who the member talks to and whom chat routes to. A prescriber
 * is not that person — ported from Pelago 1, where collapsing the separate
 * prescriber slot into one primary did not make it a better default — and
 * neither is a physician, for the same reason rather than a different one.
 *
 * Deliberately a SET rather than a single value: the question every consumer
 * asks is membership, and a single value made "which roles are ineligible" a
 * decision nobody was asked to make when `physician` entered the vocabulary.
 *
 * Deliberately POLICY rather than a schema or database constraint, so care-ops
 * can lift it by editing this array with no migration — and enforced in the
 * service rather than by hiding a button, because a hidden control is only a
 * suggestion. It lives here rather than beside either enforcement point because
 * the server refusal and the staff-UI predicates must never be able to disagree.
 *
 * It gates who may hold the primary SLOT and nothing else. It is not an
 * entitlement check (whether a person may hold a role at all — that is the
 * clinical-role grant set) and it is never an authorization input (ADR-087
 * Rule 1).
 */
export const PRIMARY_INELIGIBLE_ROLES: readonly ClinicianRole[] = ['prescriber', 'physician'];

/** One open assignment, as staff surfaces see it. */
export const careTeamAssignmentSchema = z.object({
  assignmentId: z.string().uuid(),
  internalUserId: z.string().uuid(),
  name: z.string(),
  photo: z.string().nullable(),
  /** Raw org role, so the client can label it however it labels org roles. */
  orgRole: z.string(),
  clinicalRole: ClinicianRole,
  isPrimary: z.boolean(),
  source: careTeamAssignmentSourceSchema,
  startedAt: z.string(),
  confirmedAt: z.string().nullable(),
});
export type CareTeamAssignment = z.infer<typeof careTeamAssignmentSchema>;

/** The staff list payload — also every write's re-read, so a client cannot hold a stale primary. */
export const careTeamListDataSchema = z.object({ assignments: z.array(careTeamAssignmentSchema) });
export type CareTeamListData = z.infer<typeof careTeamListDataSchema>;

export const careTeamListResponseSchema = apiEnvelope(careTeamListDataSchema);
export type CareTeamListResponse = z.infer<typeof careTeamListResponseSchema>;

/**
 * `POST /api/v1/internal/members/{ref}/care-team`.
 *
 * The clinical role is explicit rather than derived from the assignee's org
 * role: the two vocabularies overlap in five values, which makes deriving look
 * free, but they are governed separately and a value legal in one is not
 * automatically legal in the other. A client may DEFAULT the field from the org
 * role; the server never infers it.
 */
export const careTeamAssignRequestSchema = z
  // Stays strict: ADR-135 relaxes RESPONSE schemas only and says so explicitly —
  // input validation is not loosened. An unexpected key here is a caller bug
  // worth rejecting, not a forward-compatibility hazard.
  .object({ internalUserId: z.string().uuid(), role: ClinicianRole })
  .strict();
export type CareTeamAssignRequest = z.infer<typeof careTeamAssignRequestSchema>;

/**
 * How many candidates one `GET …/care-team/candidates` response may carry.
 *
 * The read is deliberately unpaged — the picker is a plain selector whose
 * results are chosen from as a whole, and a partial list is not merely short: it
 * can make the modal state that everyone else is already on the member's team
 * while unassigned clinicians sit outside the window. That is a false statement
 * about the member's care team, which is the one thing this feature exists to
 * stop. The bound is therefore set far above any plausible clinician headcount
 * and paired with `truncated`, so the honest answer to "there are more" is a
 * sentence the operator reads rather than a silence they cannot see.
 */
export const CARE_TEAM_CANDIDATES_LIMIT = 200;

/**
 * One person the member may be assigned to.
 *
 * A strict PROJECTION of `internal_users`, not a staff directory row. The
 * surplus the provisioning roster carries — email, provisioning status, teams,
 * photo, bio — is exactly what justified gating that roster at `manager|admin`,
 * so removing the surplus is what lets this read sit behind the same gate as the
 * write it feeds — now the clinical WRITE tier rather than `atlas_enabled`,
 * which is the same argument one tier further in. The schema is deliberately NOT
 * `.strict()` — ADR-135 Rule 1 forbids that on a response a client parses — so
 * a re-added column is STRIPPED here rather than rejected. Stripping is what
 * keeps it off the wire: the route emits parser output only, so a field this
 * object does not declare cannot be serialized even if the query selects it.
 * The ADR-066 Rule 3 drift guard ties the field set to the live table.
 *
 * `orgRole` is the raw `internal_users.role` so the client can label it however
 * it labels org roles — a display field and nothing more. It is deliberately
 * NOT a source for the clinical role: deriving one from the other is the guess
 * `assignableRoles` replaced, and for an assignee with a `care_specialists` row
 * the guess offered roles the write refuses. The two vocabularies are governed
 * separately (ADR-087 Rule 4), and the clinical role on an assignment stays an
 * explicit operator choice from `assignableRoles`.
 */
export const careTeamCandidateSchema = z.object({
  internalUserId: z.string().uuid(),
  /** Resolved server-side by the shared display-name ladder, never null. */
  name: z.string(),
  orgRole: z.string(),
  /**
   * The clinical roles this person may hold on THIS member's care team.
   *
   * Server-answered rather than client-guessed, because the server already
   * has an opinion the client was ignoring: an assignee with an active
   * `care_specialists` row is refused any role but that row's
   * `clinical_role` (`role-mismatch-with-specialist`), so a selector offering
   * all five clinical roles was offering four the server would reject.
   *
   * Non-empty by contract. A person the server cannot name at least one role
   * for is not a candidate at all and is omitted from `candidates`, so the
   * client never has to render an unselectable row.
   *
   * This is a UI narrowing, NOT an authorization control. A direct `POST` with
   * an off-list role is still accepted for anyone without a `care_specialists`
   * row — `care-team-write-service` owns that enforcement and its own docblock
   * makes the point that a hidden control is a suggestion. Enforcing a
   * per-person grant is a separate change; when it lands it swaps the source
   * behind this field and touches no client code.
   */
  assignableRoles: z.array(ClinicianRole).min(1),
});
export type CareTeamCandidate = z.infer<typeof careTeamCandidateSchema>;

/**
 * `GET /api/v1/internal/members/{ref}/care-team/candidates`.
 *
 * `eligibleTotal` counts the clinicians eligible for a care team across the
 * organisation, BEFORE this member's open assignees are excluded. It exists
 * because two empty states mean different things and the modal has always said
 * so: "no one holds a care-team role yet" and "everyone eligible is already on
 * this member's team" are both an empty `candidates` array, and conflating them
 * tells the operator something false. When the server took over the
 * already-assigned filter the client lost the roster it used to tell them apart,
 * so the count comes back with the list.
 */
export const careTeamCandidatesDataSchema = z.object({
  candidates: z.array(careTeamCandidateSchema),
  /** The eligible-and-unassigned set exceeded `CARE_TEAM_CANDIDATES_LIMIT`. */
  truncated: z.boolean(),
  eligibleTotal: z.number().int().nonnegative(),
});
export type CareTeamCandidatesData = z.infer<typeof careTeamCandidatesDataSchema>;

export const careTeamCandidatesResponseSchema = apiEnvelope(careTeamCandidatesDataSchema);
export type CareTeamCandidatesResponse = z.infer<typeof careTeamCandidatesResponseSchema>;

/**
 * The longest search term `GET …/care-team/candidates?q=` accepts, after trim.
 *
 * Far past any name or email a person types, and a bound on how much text the
 * search predicate has to scan. The client caps its input at the same length so
 * it can never send a term the server refuses.
 */
export const CARE_TEAM_CANDIDATES_QUERY_MAX_LENGTH = 100;

/**
 * The query string of `GET …/care-team/candidates`.
 *
 * `q` narrows the candidates to people whose name or email contains it,
 * case-insensitively, and is applied BEFORE the cap, so a person who sorts past
 * `CARE_TEAM_CANDIDATES_LIMIT` is still findable. Trimmed here so the client
 * and the server agree on what an empty search is: a blank `q` is no search.
 *
 * `.strict()` because this is a REQUEST schema (ADR-135 permits it there): an
 * unknown parameter is refused rather than silently ignored, so a misspelt
 * `query=` cannot read as "no filter".
 */
export const careTeamCandidatesQuerySchema = z
  .object({
    q: z.string().trim().max(CARE_TEAM_CANDIDATES_QUERY_MAX_LENGTH).optional(),
  })
  .strict();
export type CareTeamCandidatesQuery = z.infer<typeof careTeamCandidatesQuerySchema>;
